import { useEffect, useState } from 'react';
import { IDataRequestSummary } from '@vivli/features/data-requests/infrastructure/interface';
import { DataRequestListTabEnum } from '@vivli/features/data-requests/infrastructure/enum';
import { DataRequestsPageComponent } from '@vivli/features/data-requests/components';
import { useActiveUserContext, useAssignedAppTypeContext, useDataRequestsApiContext } from '@vivli/core/infrastructure/context';
import { ITab } from '@vivli/shared/infrastructure/interface';
import { useDataRequestDefaultTabHook } from '@vivli/features/data-requests/infrastructure/hook';
import { useNavigate, useParams } from 'react-router';
import { useOrganizations } from '@vivli/features/organizations/infrastructure/hook';
import { useUserPermissions } from '@vivli/features/users/infrastructure/hook';

enum TabTitleEnum {
    awaitingMyAction = 'Awaiting My Action',
    draft = 'Draft',
    active = 'Active',
    notApproved = 'Not Approved',
    withdrawn = 'Withdrawn',
    archived = 'Archived'
}

enum TabRouteEnum {
    archived = 'archived',
    withdrawn = 'withdrawn',
    notApproved = 'notapproved',
    active = 'active',
    draft = 'draft',
    awaitingMyAction = 'awaitingmyaction'
}

// DEV-1003: each tab route pages on the server as this tab.
const serverTabs: Record<string, DataRequestListTabEnum> = {
    [TabRouteEnum.awaitingMyAction]: DataRequestListTabEnum.AwaitingMyAction,
    [TabRouteEnum.draft]: DataRequestListTabEnum.Draft,
    [TabRouteEnum.active]: DataRequestListTabEnum.Active,
    [TabRouteEnum.notApproved]: DataRequestListTabEnum.NotApproved,
    [TabRouteEnum.withdrawn]: DataRequestListTabEnum.Withdrawn,
    [TabRouteEnum.archived]: DataRequestListTabEnum.Archived
};

// Tabs carry no data or count: each tab's grid fetches its own pages, and its count comes from
// the server's total for that tab.
const getTabs = (isPresumedResearcher?: boolean) => {
    const awaitingMyActionTab: ITab<IDataRequestSummary[]> = {
        id: TabRouteEnum.awaitingMyAction,
        sortOrder: 0,
        title: TabTitleEnum.awaitingMyAction
    };

    const defaultTabs: ITab<IDataRequestSummary[]>[] = [
        {
            id: TabRouteEnum.draft,
            sortOrder: 1,
            title: TabTitleEnum.draft,
            hint: isPresumedResearcher ? 'Requests you have not submitted yet' : 'Requests not yet submitted',
            initialSortField: 'createdTime',
            initialSortOrder: 'desc'
        },
        {
            id: TabRouteEnum.active,
            sortOrder: 2,
            title: TabTitleEnum.active,
            hint: isPresumedResearcher
                ? 'Approved requests, and requests awaiting approvals, data upload, or resubmission'
                : 'Requests awaiting approvals or Data Package upload'
        },
        {
            id: TabRouteEnum.notApproved,
            sortOrder: 4,
            title: TabTitleEnum.notApproved
        },
        {
            id: TabRouteEnum.withdrawn,
            sortOrder: 5,
            title: TabTitleEnum.withdrawn
        },
        {
            id: TabRouteEnum.archived,
            sortOrder: 6,
            title: TabTitleEnum.archived
        }
    ];

    //if not is presumed researcher, add awaiting my action tab to the beginning of the tabs array
    if (!isPresumedResearcher) {
        defaultTabs.unshift(awaitingMyActionTab);
    }
    return defaultTabs.sort((a, b) => a.sortOrder - b.sortOrder);
};

export const DataRequestsFeature = () => {
    const [subHeader, setSubHeader] = useState<string>();
    const [tabs, setTabs] = useState<ITab<IDataRequestSummary[]>[]>();
    const [defaultTab, setDefaultTab] = useState<string>();
    const { user } = useActiveUserContext();
    const { isPresumedResearcher } = useUserPermissions();
    const assignedAppType = useAssignedAppTypeContext();
    const { getDataRequest } = useDataRequestsApiContext();
    const navigate = useNavigate();
    const { status } = useParams();
    const { organizations } = useOrganizations();
    const { getDefaultTabRoute } = useDataRequestDefaultTabHook();

    const getDefaultRequestsTab = () => (isPresumedResearcher ? TabRouteEnum.draft : TabRouteEnum.awaitingMyAction);

    const initDefaultTab = () => {
        const tabRoute = status?.toLowerCase();
        const nextDefaultTab = tabRoute?.length ? tabRoute : getDefaultRequestsTab();

        setDefaultTab(nextDefaultTab);

        if (!tabRoute) {
            navigate(`/admin/requests/${nextDefaultTab}`, { replace: true });
        }
    };

    const handleRowClick = (dataRequestSummary: IDataRequestSummary) => {
        getDataRequest(dataRequestSummary.id).subscribe({
            next: (dataRequest) => {
                const defaultRoute = getDefaultTabRoute(dataRequest, organizations, status);
                navigate(defaultRoute);
            }
        });
    };

    const handleTabClick = (tab: ITab<IDataRequestSummary[]>) => {
        navigate(`/admin/requests/${tab.id}`);
    };

    useEffect(() => {
        initDefaultTab();
        setTabs(getTabs(isPresumedResearcher));
    }, [status, user, isPresumedResearcher]);

    useEffect(() => {
        if (!user) {
            return;
        }
        //TODO PERMFE - redo with permissions+
        const subHeaderStr = 'Data Requests for studies provided by Vivli Members';

        setSubHeader(subHeaderStr);
    }, [user]);

    return (
        <DataRequestsPageComponent
            title="Data Requests"
            isPresumedResearcher={isPresumedResearcher}
            subHeader={subHeader}
            tabs={tabs}
            defaultTab={defaultTab}
            showExport={true}
            onRowClick={handleRowClick}
            onTabClick={handleTabClick}
            hideFloatingFilter={true}
            serverPaging={{ assignedAppType: assignedAppType.type, serverTabs }}
        />
    );
};
